import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search, MapPin, Check, X, Globe } from 'lucide-react';

// Données des territoires d'outre-mer français avec emojis et métadonnées
const TERRITOIRES_OUTRE_MER = [
  {
    region: 'france_metropolitaine',
    label: 'France Métropolitaine',
    emoji: '🇫🇷',
    description: 'Hexagone',
    territoires: [
      { code: 'metropole', nom: 'France Métropolitaine', departement: '', emoji: '🗼', description: 'Territoire européen' }
    ]
  },
  {
    region: 'antilles',
    label: 'Antilles',
    emoji: '🌴',
    description: 'Mer des Caraïbes',
    territoires: [
      { code: 'guadeloupe', nom: 'Guadeloupe', departement: '971', emoji: '🦜', description: 'DROM - Archipel des Caraïbes' },
      { code: 'martinique', nom: 'Martinique', departement: '972', emoji: '🌺', description: 'DROM - Île aux fleurs' },
      { code: 'saint_martin', nom: 'Saint-Martin', departement: '978', emoji: '🏝️', description: 'COM - Partie française' },
      { code: 'saint_barthelemy', nom: 'Saint-Barthélemy', departement: '977', emoji: '⛵', description: 'COM - Île des Antilles' }
    ]
  },
  {
    region: 'guyane',
    label: 'Amérique du Sud',
    emoji: '🌿',
    description: 'Amazonie',
    territoires: [
      { code: 'guyane', nom: 'Guyane', departement: '973', emoji: '🚀', description: 'DROM - Amérique du Sud' }
    ]
  },
  {
    region: 'ocean_indien',
    label: 'Océan Indien',
    emoji: '🌊',
    description: 'Sud-Ouest de l\'océan Indien',
    territoires: [
      { code: 'reunion', nom: 'La Réunion', departement: '974', emoji: '🌋', description: 'DROM - Île volcanique' },
      { code: 'mayotte', nom: 'Mayotte', departement: '976', emoji: '🐢', description: 'DROM - Archipel des Comores' }
    ]
  },
  {
    region: 'pacifique',
    label: 'Pacifique',
    emoji: '🏄',
    description: 'Océan Pacifique Sud',
    territoires: [
      { code: 'nouvelle_caledonie', nom: 'Nouvelle-Calédonie', departement: '988', emoji: '🦎', description: 'Collectivité sui generis' },
      { code: 'polynesie_francaise', nom: 'Polynésie française', departement: '987', emoji: '🌺', description: 'COM - 118 îles' },
      { code: 'wallis_et_futuna', nom: 'Wallis-et-Futuna', departement: '986', emoji: '🐚', description: 'COM - Archipel' }
    ]
  },
  {
    region: 'atlantique_nord',
    label: 'Atlantique Nord',
    emoji: '❄️',
    description: 'Amérique du Nord',
    territoires: [
      { code: 'saint_pierre_et_miquelon', nom: 'Saint-Pierre-et-Miquelon', departement: '975', emoji: '🎣', description: 'COM - Proche du Canada' }
    ]
  },
  {
    region: 'terres_australes',
    label: 'Terres Australes',
    emoji: '🐧',
    description: 'Antarctique français',
    territoires: [
      { code: 'taaf', nom: 'Terres australes et antarctiques françaises', departement: '984', emoji: '🧊', description: 'Territoire d\'outre-mer' }
    ]
  }
];

const TerritoireSelector = ({ value, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Fermer le dropdown si clic en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus sur la recherche quand on ouvre
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filtrer les territoires selon la recherche
  const filteredTerritoires = useMemo(() => {
    if (!searchQuery.trim()) return TERRITOIRES_OUTRE_MER;
    
    const query = searchQuery.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    return TERRITOIRES_OUTRE_MER.map(region => ({
      ...region,
      territoires: region.territoires.filter(t => 
        t.nom.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(query) ||
        t.departement.includes(query) ||
        t.description.toLowerCase().includes(query)
      )
    })).filter(region => region.territoires.length > 0);
  }, [searchQuery]);

  // Trouver le territoire sélectionné
  const selectedTerritoire = useMemo(() => {
    if (!value) return null;
    for (const region of TERRITOIRES_OUTRE_MER) {
      const found = region.territoires.find(t => t.code === value);
      if (found) return { ...found, region: region.label };
    }
    return null;
  }, [value]);

  const handleSelect = (territoire) => {
    onChange(territoire.code);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full h-12 px-4 text-left rounded-xl border-2 transition-all duration-200
          flex items-center justify-between gap-3
          ${isOpen 
            ? 'border-purple-500 ring-4 ring-purple-100 bg-white shadow-lg' 
            : 'border-slate-200 bg-gradient-to-r from-slate-50 to-white hover:border-purple-300 hover:shadow-md'
          }
          ${selectedTerritoire ? 'pr-10' : ''}
        `}
        data-testid="territoire-intervention-select"
      >
        {selectedTerritoire ? (
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-2xl flex-shrink-0">{selectedTerritoire.emoji}</span>
            <div className="min-w-0">
              <div className="font-semibold text-slate-800 truncate">{selectedTerritoire.nom}</div>
              <div className="text-xs text-slate-500 truncate">{selectedTerritoire.region} • {selectedTerritoire.departement || 'Métropole'}</div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 text-slate-400">
            <Globe className="h-5 w-5" />
            <span>Sélectionnez un territoire</span>
          </div>
        )}
        
        <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
        
        {/* Clear button */}
        {selectedTerritoire && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-10 p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Search */}
          <div className="p-3 border-b border-slate-100 bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un territoire..."
                className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition-all"
              />
            </div>
          </div>

          {/* Territories List */}
          <div className="max-h-80 overflow-y-auto overscroll-contain">
            {filteredTerritoires.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                <MapPin className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                <p>Aucun territoire trouvé</p>
              </div>
            ) : (
              filteredTerritoires.map((region) => (
                <div 
                  key={region.region}
                  onMouseEnter={() => setHoveredRegion(region.region)}
                  onMouseLeave={() => setHoveredRegion(null)}
                >
                  {/* Region Header */}
                  <div className={`
                    sticky top-0 px-4 py-2 text-xs font-bold uppercase tracking-wider
                    flex items-center gap-2 backdrop-blur-sm transition-colors duration-200
                    ${hoveredRegion === region.region 
                      ? 'bg-purple-100/90 text-purple-700' 
                      : 'bg-slate-50/90 text-slate-500'
                    }
                  `}>
                    <span className="text-base">{region.emoji}</span>
                    <span>{region.label}</span>
                    <span className="text-slate-400 font-normal ml-auto">{region.description}</span>
                  </div>

                  {/* Territories in Region */}
                  {region.territoires.map((territoire) => (
                    <button
                      key={territoire.code}
                      type="button"
                      onClick={() => handleSelect(territoire)}
                      className={`
                        w-full px-4 py-3 text-left flex items-center gap-3
                        transition-all duration-150 group
                        ${value === territoire.code
                          ? 'bg-purple-50 border-l-4 border-purple-500'
                          : 'hover:bg-gradient-to-r hover:from-purple-50 hover:to-transparent border-l-4 border-transparent hover:border-purple-300'
                        }
                      `}
                    >
                      <span className="text-xl group-hover:scale-110 transition-transform duration-200">
                        {territoire.emoji}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium ${value === territoire.code ? 'text-purple-700' : 'text-slate-700'}`}>
                          {territoire.nom}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-2">
                          {territoire.departement && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">
                              {territoire.departement}
                            </span>
                          )}
                          <span>{territoire.description}</span>
                        </div>
                      </div>
                      {value === territoire.code && (
                        <Check className="h-5 w-5 text-purple-500 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-slate-100 bg-gradient-to-r from-slate-50 to-white">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {TERRITOIRES_OUTRE_MER.reduce((acc, r) => acc + r.territoires.length, 0)} territoires disponibles
              </span>
              <span className="text-purple-600 font-medium">DROM-COM</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerritoireSelector;
